import React, { useCallback, useMemo, useState } from 'react';
import {
    App as AntdApp,
    Button,
    Card,
    Form,
    Input,
    Popconfirm,
    Select,
    Space,
    Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { RealtimeConfigCategory } from '../entities/RealtimeConfig';
import type { KmsSaveResult } from '../entities/Kms';
import { KmsService } from '../http/KmsService';
import { RealtimeConfigService } from '../http/RealtimeConfigService';
import './KmsPage.css';

const { Text, Title } = Typography;

const DEFAULT_APP_KEY = 'com.haiskynology.sjreet.mall';
const CATEGORY_OPTIONS: Array<{ label: RealtimeConfigCategory; value: RealtimeConfigCategory }> = [
    { label: 'GENERAL', value: 'GENERAL' },
    { label: 'PHONE', value: 'PHONE' },
    { label: 'ADDRESS', value: 'ADDRESS' },
    { label: 'BIRTHDAY', value: 'BIRTHDAY' },
];

type KmsFormValues = {
    appKey: string;
    category: RealtimeConfigCategory;
    key?: string;
    value?: string;
};

type RsaFormValues = {
    appKey: string;
    publicKey?: string;
    encryptedValue?: string;
};

type KmsResultState = {
    saveResult?: KmsSaveResult;
    plainValue?: string | null;
    token?: string | null;
};

function getErrorMessage(error: unknown, fallback: string) {
    return error instanceof Error ? error.message : fallback;
}

export default function KmsPage() {
    const { message } = AntdApp.useApp();
    const navigate = useNavigate();
    const [kmsForm] = Form.useForm<KmsFormValues>();
    const [rsaForm] = Form.useForm<RsaFormValues>();
    const [loading, setLoading] = useState(false);
    const [rsaLoading, setRsaLoading] = useState(false);
    const [result, setResult] = useState<KmsResultState>({});
    const [rsaDecryptedValue, setRsaDecryptedValue] = useState<string | null>(null);
    const [appKeys, setAppKeys] = useState<string[]>([]);

    const appKey = Form.useWatch('appKey', kmsForm) ?? DEFAULT_APP_KEY;
    const category = Form.useWatch('category', kmsForm) ?? 'GENERAL';

    const initialKmsValues = useMemo<KmsFormValues>(
        () => ({
            appKey: DEFAULT_APP_KEY,
            category: 'GENERAL',
            key: '',
            value: '',
        }),
        []
    );

    const initialRsaValues = useMemo<RsaFormValues>(
        () => ({
            appKey: DEFAULT_APP_KEY,
            publicKey: '',
            encryptedValue: '',
        }),
        []
    );

    const appKeyOptions = useMemo(
        () => appKeys.map((value) => ({ label: value, value, title: value })),
        [appKeys]
    );

    React.useEffect(() => {
        let mounted = true;
        RealtimeConfigService.listRealtimeConfigAppKeys()
            .then((list) => {
                if (!mounted) {
                    return;
                }
                const nextAppKeys = list.length > 0 ? list : [DEFAULT_APP_KEY];
                setAppKeys(nextAppKeys);
                kmsForm.setFieldValue('appKey', nextAppKeys[0]);
                rsaForm.setFieldValue('appKey', nextAppKeys[0]);
            })
            .catch((error) => {
                if (!mounted) {
                    return;
                }
                setAppKeys([DEFAULT_APP_KEY]);
                message.error(getErrorMessage(error, '获取 AppKey 失败'));
            });
        return () => {
            mounted = false;
        };
    }, [kmsForm, message, rsaForm]);

    const withKmsAction = useCallback(
        async (action: () => Promise<void>, fallback: string) => {
            setLoading(true);
            try {
                await action();
            } catch (error) {
                message.error(getErrorMessage(error, fallback));
            } finally {
                setLoading(false);
            }
        },
        [message]
    );

    const saveByKey = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'key', 'value']);
        await withKmsAction(async () => {
            const saved = await KmsService.save(values.appKey.trim(), values.category, values.key?.trim() ?? '', values.value ?? '');
            setResult({ saveResult: saved });
            message.success('密文已保存');
        }, '保存密文失败');
    }, [kmsForm, message, withKmsAction]);

    const saveWithToken = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'value']);
        await withKmsAction(async () => {
            const saved = await KmsService.saveWithToken(values.appKey.trim(), values.category, values.value ?? '');
            kmsForm.setFieldValue('key', saved.key);
            setResult({ saveResult: saved, token: saved.key });
            message.success('Token 已生成');
        }, '生成 Token 失败');
    }, [kmsForm, message, withKmsAction]);

    const updateWithToken = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'key', 'value']);
        await withKmsAction(async () => {
            const saved = await KmsService.updateWithToken(values.appKey.trim(), values.category, values.key?.trim() ?? '', values.value ?? '');
            kmsForm.setFieldValue('key', saved.key);
            setResult({ saveResult: saved, token: saved.key });
            message.success('Token 密文已更新');
        }, '更新 Token 失败');
    }, [kmsForm, message, withKmsAction]);

    const getValue = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'key']);
        await withKmsAction(async () => {
            const plainValue = await KmsService.getValue(values.appKey.trim(), values.category, values.key?.trim() ?? '');
            setResult({ plainValue });
            message.success('明文已读取');
        }, '读取明文失败');
    }, [kmsForm, message, withKmsAction]);

    const getTokenByValue = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'value']);
        await withKmsAction(async () => {
            const token = await KmsService.selectTokenByValue(values.appKey.trim(), values.category, values.value ?? '');
            if (token) {
                kmsForm.setFieldValue('key', token);
            }
            setResult({ token });
            message.success(token ? 'Token 已找到' : '没有找到匹配 Token');
        }, '查询 Token 失败');
    }, [kmsForm, message, withKmsAction]);

    const deleteByKey = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'key']);
        await withKmsAction(async () => {
            await KmsService.deleteByKey(values.appKey.trim(), values.category, values.key?.trim() ?? '');
            setResult({});
            message.success('密文已删除');
        }, '删除密文失败');
    }, [kmsForm, message, withKmsAction]);

    const deleteByToken = useCallback(async () => {
        const values = await kmsForm.validateFields(['appKey', 'category', 'key']);
        await withKmsAction(async () => {
            await KmsService.deleteByToken(values.appKey.trim(), values.category, values.key?.trim() ?? '');
            setResult({});
            message.success('Token 已删除');
        }, '删除 Token 失败');
    }, [kmsForm, message, withKmsAction]);

    const generatePublicKey = useCallback(async () => {
        const values = await rsaForm.validateFields(['appKey']);
        setRsaLoading(true);
        try {
            const publicKey = await KmsService.generateRsaPublicKey(values.appKey.trim());
            rsaForm.setFieldValue('publicKey', publicKey);
            setRsaDecryptedValue(null);
            message.success('RSA 公钥已生成');
        } catch (error) {
            message.error(getErrorMessage(error, '生成 RSA 公钥失败'));
        } finally {
            setRsaLoading(false);
        }
    }, [message, rsaForm]);

    const decryptByPublicKey = useCallback(async () => {
        const values = await rsaForm.validateFields(['appKey', 'publicKey', 'encryptedValue']);
        setRsaLoading(true);
        try {
            const decrypted = await KmsService.decryptByRsaPublicKey(
                values.appKey.trim(),
                values.publicKey?.trim() ?? '',
                values.encryptedValue ?? ''
            );
            setRsaDecryptedValue(decrypted);
            message.success('密文已解密');
        } catch (error) {
            message.error(getErrorMessage(error, 'RSA 解密失败'));
        } finally {
            setRsaLoading(false);
        }
    }, [message, rsaForm]);

    return (
        <div className="kms-page">
            <div className="kms-layout">
                <Card
                    className="kms-panel"
                    title={
                        <Space>
                            <Button type="link" className="page-back-button" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
                            <span>KMS 密钥管理</span>
                        </Space>
                    }
                >
                    <Form form={kmsForm} layout="vertical" initialValues={initialKmsValues}>
                        <div className="kms-form-grid">
                            <Form.Item name="appKey" label="App Key" rules={[{ required: true, message: '请输入 App Key' }]}>
                                <Select showSearch options={appKeyOptions} />
                            </Form.Item>
                            <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
                                <Select options={CATEGORY_OPTIONS} />
                            </Form.Item>
                        </div>
                        <Form.Item name="key" label="Key / Token">
                            <Input placeholder="命名密钥填写 key，Token 操作填写 token" />
                        </Form.Item>
                        <Form.Item name="value" label="明文值">
                            <Input.TextArea rows={8} placeholder="需要保存、更新或查 token 的明文" />
                        </Form.Item>
                        <Space wrap className="kms-actions">
                            <Button type="primary" loading={loading} onClick={() => void saveByKey()}>
                                保存密文
                            </Button>
                            <Button loading={loading} onClick={() => void saveWithToken()}>
                                生成 Token
                            </Button>
                            <Button loading={loading} onClick={() => void updateWithToken()}>
                                更新 Token
                            </Button>
                            <Button loading={loading} onClick={() => void getValue()}>
                                读取明文
                            </Button>
                            <Button loading={loading} onClick={() => void getTokenByValue()}>
                                查 Token
                            </Button>
                            <Popconfirm title="确认删除这个 key？" onConfirm={() => void deleteByKey()}>
                                <Button danger loading={loading}>
                                    删除 Key
                                </Button>
                            </Popconfirm>
                            <Popconfirm title="确认删除这个 token？" onConfirm={() => void deleteByToken()}>
                                <Button danger loading={loading}>
                                    删除 Token
                                </Button>
                            </Popconfirm>
                        </Space>
                    </Form>

                    <section className="kms-result">
                        <Title level={5}>操作结果</Title>
                        <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <Text type="secondary">当前作用域：{appKey} / {category}</Text>
                            <Input addonBefore="Key / Token" value={result.saveResult?.key ?? result.token ?? ''} readOnly />
                            <Input.TextArea
                                rows={4}
                                value={result.saveResult?.encryptedValue ?? result.plainValue ?? ''}
                                readOnly
                                placeholder="密文或明文结果"
                            />
                        </Space>
                    </section>
                </Card>

                <Card className="kms-panel" title="RSA 临时密钥">
                    <Form form={rsaForm} layout="vertical" initialValues={initialRsaValues}>
                        <Form.Item name="appKey" label="App Key" rules={[{ required: true, message: '请输入 App Key' }]}>
                            <Select showSearch options={appKeyOptions} />
                        </Form.Item>
                        <Form.Item name="publicKey" label="公钥" rules={[{ required: true, message: '请先生成或输入公钥' }]}>
                            <Input.TextArea rows={6} />
                        </Form.Item>
                        <Form.Item name="encryptedValue" label="密文" rules={[{ required: true, message: '请输入密文' }]}>
                            <Input.TextArea rows={8} />
                        </Form.Item>
                        <Space wrap>
                            <Button type="primary" loading={rsaLoading} onClick={() => void generatePublicKey()}>
                                生成公钥
                            </Button>
                            <Button loading={rsaLoading} onClick={() => void decryptByPublicKey()}>
                                解密密文
                            </Button>
                        </Space>
                    </Form>

                    <div className="kms-rsa-output">
                        <Title level={5}>解密结果</Title>
                        <Input.TextArea rows={5} value={rsaDecryptedValue ?? ''} readOnly />
                    </div>
                </Card>
            </div>
        </div>
    );
}
